import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faSave,
  faWindowClose,
} from '@fortawesome/free-solid-svg-icons';
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from 'react';
import {
  Card,
  Form,
  Button,
  ButtonGroup,
  InputGroup,
  Modal,
  Spinner,
  Col,
  Row,
  Image,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { updateRosterLine, addRosterLine } from '../../utils/rosterLineCalls';
import RingInputSection from '../RingInputSection/RingInputSection';
import PackageComparisonTable from '../PackageComparisonTable/PackageComparisonTable';
import championsPackageImage from '../../assets/images/championspackage_2025.jpg';
import dynastyPackageImage from '../../assets/images/dynastypackage_2025.jpg';
import ultimatePackageImage from '../../assets/images/Ultimate_PKG_Layout.png';
import nameplateImage from '../../assets/images/Nameplate_Customizable_Text.jpg';
import championsPlusPackageImage from '../../assets/images/ChampionsPlus_2025.jpg';
import championsPlusPackageNameplateImage from '../../assets/images/ChampionsPlus_Nameplate_Customization.png';

import getStates from '../../utils/getState';
import './RingSelectionView.css';
import getCountries from '../../utils/getCountry';
import { UserContext } from '../../contexts/UserContext';
import {
  ProductTypeValue,
  ProductTypeLabel,
} from '../../enums/ProductTypeValue';

const PACKAGE_BENEFITS = {
  engravingAndPolishing: {
    id: 'engraving-and-polishing',
    text: 'Inside engraving + polishing cloth',
  },
  jumboDisplayBox: {
    id: 'jumbo-display-box',
    text: 'Jumbo LED display box',
  },
  championsPlusNameplateBox: {
    id: 'nameplate-box',
    text: 'Jumbo LED display box + nameplate',
  },
  lifetimeWarranty: {
    id: 'lifetime-warranty',
    text: 'Lifetime warranty (Champions is limited)',
  },
  resizingForLife: {
    id: 'resizing-for-life',
    text: 'Resizing for life \u2014 fits forever',
  },
  ringLossInsurance: {
    id: 'ring-loss-insurance',
    text: 'Ring loss insurance',
  },
  dynastyNameplateBox: {
    id: 'nameplate-box',
    text: 'Custom nameplate box',
  },
  ultimateExtras: {
    id: 'ultimate-extras',
    text: 'Custom t-shirt, drawstring bag & jersey # pendant',
  },
};

const PACKAGE_CARD_DETAILS = [
  {
    name: 'Champions',
    label: 'Most Popular',
    image: championsPackageImage,
    savings: 45,
    defaultPreviousPackages: ['Basic'],
    valueLead: 'EVERYTHING IN BASIC, PLUS',
    includedBenefits: [
      PACKAGE_BENEFITS.engravingAndPolishing,
      PACKAGE_BENEFITS.jumboDisplayBox,
    ],
    defaultBenefits: [
      PACKAGE_BENEFITS.engravingAndPolishing,
      PACKAGE_BENEFITS.jumboDisplayBox,
    ],
  },
  {
    name: 'Champions+',
    label: 'New',
    image: championsPlusPackageImage,
    savings: 150,
    defaultPreviousPackages: ['Basic', 'Champions'],
    valueLead: 'SAME AS CHAMPIONS, BUT WITH NAMEPLATE',
    includedBenefits: [
      PACKAGE_BENEFITS.engravingAndPolishing,
      PACKAGE_BENEFITS.championsPlusNameplateBox,
    ],
    defaultBenefits: [
      PACKAGE_BENEFITS.engravingAndPolishing,
      PACKAGE_BENEFITS.championsPlusNameplateBox,
    ],
  },
  {
    name: 'Dynasty',
    label: 'Coach’s Choice',
    image: dynastyPackageImage,
    savings: 175,
    defaultPreviousPackages: ['Basic', 'Champions'],
    valueLead: 'EVERYTHING IN CHAMPIONS, PLUS',
    includedBenefits: [
      PACKAGE_BENEFITS.engravingAndPolishing,
      PACKAGE_BENEFITS.jumboDisplayBox,
      PACKAGE_BENEFITS.lifetimeWarranty,
      PACKAGE_BENEFITS.resizingForLife,
      PACKAGE_BENEFITS.ringLossInsurance,
      PACKAGE_BENEFITS.dynastyNameplateBox,
    ],
    defaultBenefits: [
      PACKAGE_BENEFITS.lifetimeWarranty,
      PACKAGE_BENEFITS.resizingForLife,
      PACKAGE_BENEFITS.ringLossInsurance,
      PACKAGE_BENEFITS.dynastyNameplateBox,
    ],
    isHero: true,
  },
  {
    name: 'Ultimate',
    label: 'Biggest Savings',
    image: ultimatePackageImage,
    savings: 210,
    defaultPreviousPackages: ['Basic', 'Champions', 'Dynasty'],
    valueLead: 'EVERYTHING IN DYNASTY, PLUS',
    includedBenefits: [
      PACKAGE_BENEFITS.engravingAndPolishing,
      PACKAGE_BENEFITS.jumboDisplayBox,
      PACKAGE_BENEFITS.lifetimeWarranty,
      PACKAGE_BENEFITS.resizingForLife,
      PACKAGE_BENEFITS.ringLossInsurance,
      PACKAGE_BENEFITS.dynastyNameplateBox,
      PACKAGE_BENEFITS.ultimateExtras,
    ],
    defaultBenefits: [PACKAGE_BENEFITS.ultimateExtras],
  },
];

const packageTitleMatches = (title, packageName) => {
  if (packageName === 'Champions') {
    return title.includes('Champions') && !title.includes('Champions+');
  }
  if (packageName === 'Basic') {
    return title.includes('Basic') || title.includes('Ring Only');
  }
  return title.includes(packageName);
};

const getPackagePrice = (packages, packageName) =>
  packages
    .filter((ringPackage) =>
      packageTitleMatches(ringPackage.title, packageName)
    )
    .reduce(
      (max, ringPackage) => Math.max(max, Number(ringPackage.price) || 0),
      0
    );

const formatPackagePrice = (price) => (price === 0 ? 'Included' : `+$${price}`);

const RingSelectionView = ({
  rosterKey,
  roster,
  playerData,
  closeModal,
  cancelEdit,
  updatePlayer,
  addPlayer,
  individualSelection,
  selectedPlayers,
  setSelectedPlayers,
  playerSelectCount,
  setPlayerSelectCount,
  internalView,
  adminUser,
  isCoach,
  editAddress,
  isCoachApproval
}) => {
  const [formKey, setFormKey] = useState(0);
  const roleHidenMaxLength = ['Admin', 'SalesManager', 'Verifier'];
  const { user } = useContext(UserContext);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submittedOrder, setSubmittedOrder] = useState(null);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [working, setWorking] = useState(false);
  const [editMode, setEditMode] = useState(
    playerData && playerData.id && playerData.id !== ''
  );
  const [initialStateSaved, setInitialStateSaved] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(
    playerData ? playerData.masterOrderItemId : ''
  );
  const [productList, setProductList] = useState([
    { title: 'Select a Product', masterOrderItemId: '' },
  ]);
  const [product, setProduct] = useState('');
  const [itemId, setItemId] = useState('');
  const [productType, setProductType] = useState('');
  const [packageProductType, setPackageProductType] = useState(false);
  const [packageHasNameplate, setPackageHasNameplate] = useState(false);
  const [packageHasAccessories, setPackageHasAccessories] = useState(false);
  const [basicPackageAvailable, setBasicPackageAvailable] = useState(false);
  const [championsPackageAvailable, setChampionsPackageAvailable] =
    useState(false);
  const [dynastyPackageAvailable, setDynastyPackageAvailable] = useState(false);
  const [ultimatePackageAvailable, setUltimatePackageAvailable] =
    useState(false);
  const [championsPlusPackageAvailable, setChamionsPlusPackageAvailable] =
    useState(false);
  const [selectedNameplateImage, setSelectedNameplateImage] = useState('');
  const [nameplateImageClass, setNameplateImageClass] = useState('');
  const [jerseyNumberPendantOptions, setJerseyNumberPendantOptions] = useState(
    []
  );
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showTooltipLastName, setShowTooltipLastName] = useState(false);
  const [valueFirstName, setValueFirstName] = useState(
    playerData.package &&
      'package' in playerData &&
      playerData.package.customizations &&
      'customizations' in playerData.package &&
      playerData.package.customizations.length > 0
      ? playerData.package.customizations[1].text
      : ''
  );
  const [valueLastName, setValueLastName] = useState(
    playerData.package &&
      'package' in playerData &&
      playerData.package.customizations &&
      'customizations' in playerData.package &&
      playerData.package.customizations.length > 0
      ? playerData.package.customizations[1].text
      : ''
  );
  const inputRef = useRef(null);
  const dynastyPackageRef = useRef(null);
  const [isShowMaxLength, setIsShowMaxLength] = useState(true);

  const handleInputClick = (value, fieldName) => {
    if (fieldName == 'First Name') {
      setShowTooltip(true);
    } else {
      setShowTooltipLastName(true);
    }
    if (value.length >= 25) {
      if (fieldName == 'First Name') {
        setShowTooltip(true);
      } else {
        setShowTooltipLastName(true);
      }
      setTimeout(() => {
        if (fieldName == 'First Name') {
          setShowTooltip(false);
        } else {
          setShowTooltipLastName(false);
        }
      }, 2000);
    }
  };

  const checkAvailablePackages = () => {
    if (
      roster.packages.filter((ringPackage) =>
        ringPackage.title.includes('Ring Only')
      ).length > 0 ||
      roster.packages.filter((ringPackage) =>
        ringPackage.title.includes('Basic')
      ).length > 0
    ) {
      setBasicPackageAvailable(true);
    }

    if (
      roster.packages.filter(
        (ringPackage) =>
          ringPackage.title.includes('Champions') &&
          !ringPackage.title.includes('Champions+')
      ).length > 0
    ) {
      setChampionsPackageAvailable(true);
    }

    if (
      roster.packages.filter((ringPackage) =>
        ringPackage.title.includes('Dynasty')
      ).length > 0
    ) {
      setDynastyPackageAvailable(true);
    }

    if (
      roster.packages.filter((ringPackage) =>
        ringPackage.title.includes('Ultimate')
      ).length > 0
    ) {
      setUltimatePackageAvailable(true);
    }

    if (
      roster.packages.filter((ringPackage) =>
        ringPackage.title.includes('Champions+')
      ).length > 0
    ) {
      setChamionsPlusPackageAvailable(true);
    }
  };

  const generateJerseyNumberPendantOptions = () => {
    let numberOptions = [];
    let outOfStockOptions = [];
    numberOptions.push('00');
    for (let i = 0; i < 100; i++) {
      if (!outOfStockOptions.includes(i)) {
        numberOptions.push(i);
      }
    }
    setJerseyNumberPendantOptions(numberOptions);
  };

  useEffect(() => {
    const fetchData = async () => {
      const countries = await getCountries();
      setCountries(countries);

      const data = await getStates(formValues.countryCode);
      setStates(data);
    };

    fetchData();

    if (user != null) {
      const isHiden = roleHidenMaxLength.some((r) => user.roles.includes(r));
      setIsShowMaxLength(!isHiden);
    }
  }, []);

  useEffect(() => {
    checkAvailablePackages();
    generateJerseyNumberPendantOptions();
  }, [roster]);

  const [formValues, setFormValues] = useState({
    sortedIndex: playerData ? playerData.sortedIndex : 0,
    id: playerData ? playerData.id : '',
    rosterId: playerData ? playerData.rosterId : roster.id,
    masterOrderItemId: playerData ? playerData.masterOrderItemId : '',
    subOrderItemRowId: playerData ? playerData.subOrderItemRowId : '',
    variation: playerData ? playerData.variation : '',
    variationId: playerData ? playerData.variationId : '',
    studentName: playerData ? playerData.studentName : '',
    studentEmail: playerData ? playerData.studentEmail : '',
    studentPhone: playerData ? playerData.studentPhone : '',
    addressLine1: playerData ? playerData.addressLine1 : '',
    addressLine2: playerData ? playerData.addressLine2 : '',
    city: playerData ? playerData.city : '',
    countryCode: playerData?.countryCode ?? 'US',
    state: playerData ? playerData.state : '',
    postalCode: playerData ? playerData.postalCode : '',
    fingerSize: playerData ? playerData.fingerSize : '',
    customizations: [],
    editable: playerData ? playerData.editable : true,
    productType: playerData ? playerData.productType : '',
    package: playerData && playerData.package ? playerData.package : null,
    optionalEngraving: playerData.optionalEngraving ?? false,
    isAddon: playerData ? playerData.isAddon : null,
    createdDate: playerData ? playerData.createdDate : null,
  });

  const [savedFormValues, setSavedFormValues] = useState({});

  const setProductObject = useCallback(() => {
    const newProduct = roster.products.filter(
      (product) => product.masterOrderItemId === selectedProduct
    );
    setProduct(newProduct[0]);
    setItemId(newProduct[0].masterOrderItemId);
    setProductType(newProduct[0].productType);
  }, [roster.products, selectedProduct]);

  useEffect(() => {
    if (isCoach == true) {
      setNameplateImageClass('nameplate');
    } else {
      setNameplateImageClass('nameplateStudent');
    }
  }, [nameplateImageClass, setNameplateImageClass]);

  useEffect(() => {
    if (roster.products) {
      setProductList(productList.concat(roster.products));

      if (roster.products.length === 1) {
        if (roster.accessLevel === 'Coach') {
          handleBatchInputChange(
            selectedPlayers,
            'masterOrderItemId',
            roster.products[0].masterOrderItemId
          );
        } else {
          handleInputChange(
            'masterOrderItemId',
            roster.products[0].masterOrderItemId
          );
        }
        setItemId(roster.products[0].masterOrderItemId);
        setSelectedProduct(roster.products[0].masterOrderItemId);
      }
    }
  }, [roster.products]);

  useEffect(() => {
    if (selectedProduct) {
      setProductObject();
    }
    return () => {};
  }, [selectedProduct, setProductObject]);

  useEffect(() => {
    if (playerData) {
      setEditMode(playerData && playerData.id && playerData.id !== '');
      if (formValues.customizations) {
        const newCustomObject = {
          id: playerData.id,
          customizations: formValues.customizations.concat(
            playerData.customizations
          ),
        };
        setFormValues({
          ...formValues,
          ...newCustomObject,
        });
      }
    }
  }, [playerData]);

  useEffect(() => {
    if (editMode && !initialStateSaved) {
      setSavedFormValues({ ...formValues });
      setInitialStateSaved(true);
    }
  }, [editMode, initialStateSaved]);

  useEffect(() => {
    if (
      productType === ProductTypeLabel[ProductTypeValue.ChampionshipRing] ||
      productType === ProductTypeLabel[ProductTypeValue.FashionRing] ||
      productType === ProductTypeLabel[ProductTypeValue.ClassRing] ||
      productType ===
        ProductTypeLabel[ProductTypeValue.RemovableTopChampionshipRing] ||
      productType ===
        ProductTypeLabel[ProductTypeValue.RemovableTopCapsuleChampionshipRing] ||
      productType === ProductTypeLabel[ProductTypeValue.Pendant]
    ) {
      setPackageProductType(true);
    } else {
      setPackageProductType(false);
    }
  }, [productType]);

  useEffect(() => {
    if (formValues.package !== null) {
      if (formValues.package.name === 'Champions+') {
        setSelectedNameplateImage(championsPlusPackageNameplateImage);
      } else {
        setSelectedNameplateImage(nameplateImage);
      }
    }
  });
  const handleInputChange = (
    inputName,
    value,
    customizations,
    customizationValues
  ) => {
    if (
      inputName === 'fingerSize' &&
      internalView &&
      roster.paymentType === 'Individual Pay' &&
      playerData.id?.startsWith('SubOrder')
    ) {
      const currentCustomizations =
        formValues.customizations.length > 0
          ? formValues.customizations
          : playerData.customizations;
      const hasFingerSizeCustomization = currentCustomizations?.some(
        (customization) =>
          customization.name.toLowerCase() === 'Finger Size'.toLowerCase()
      );

      if (hasFingerSizeCustomization) {
        const updatedCustomizations = currentCustomizations.map((customization) =>
          customization.name.toLowerCase() === 'Finger Size'.toLowerCase()
            ? { ...customization, text: value }
            : customization
        );

        setFormValues({
          ...formValues,
          fingerSize: null,
          customizations: updatedCustomizations,
          productType: productType,
        });
        return;
      }
    }

    if (inputName === 'package') {
      if (
        value.name === 'Dynasty' ||
        value.name === 'Ultimate' ||
        value.name === 'Champions+'
      ) {
        if (value.name === 'Champions+') {
          setSelectedNameplateImage(championsPlusPackageNameplateImage);
        } else {
          setSelectedNameplateImage(nameplateImage);
        }

        if (document.getElementById('nameplateFirstName')) {
          document.getElementById('nameplateFirstName').value = '';
          document.getElementById('nameplateLastName').value = '';
          if (
            document.getElementById('nameplateFirstName').value !== '' &&
            document.getElementById('nameplateFirstName').value !== null &&
            document.getElementById('nameplateLastName').value !== '' &&
            document.getElementById('nameplateLastName').value !== null
          ) {
            if (document.getElementById('add')) {
              document.getElementById('add').removeAttribute('disabled');
            }
            if (document.getElementById('update')) {
              document.getElementById('update').removeAttribute('disabled');
            }
          } else {
            if (document.getElementById('add')) {
              document.getElementById('add').disabled = true;
            }

            if (document.getElementById('update')) {
              document.getElementById('update').disabled = true;
            }
          }
        } else {
          if (document.getElementById('add')) {
            document.getElementById('add').disabled = true;
          }
          if (document.getElementById('update')) {
            document.getElementById('update').disabled = true;
          }
        }
      } else {
        if (document.getElementById('add')) {
          document.getElementById('add').removeAttribute('disabled');
        }
        if (document.getElementById('update')) {
          document.getElementById('update').removeAttribute('disabled');
        }
      }
    } else if (inputName === 'countryCode') {
      const fetchData = async () => {
        const data = await getStates(value);
        setStates(data);
      };
      if (value === '') {
        setStates([]);
      } else {
        fetchData();
      }
    }

    if (inputName === 'masterOrderItemId') {
      const newProduct = roster.products.find(
        (product) => product.masterOrderItemId === value
      );

      let writtenName = '';
      let htmlName = '';
      let newPackageValue = null;
      setPackageHasNameplate(false);
      setPackageHasAccessories(false);

      product.customizations?.forEach((customization) => {
        writtenName = customization.name;
        htmlName = convertCustomizationName(writtenName);
        if (document.getElementsByName(htmlName)[0]) {
          console.log(
            htmlName + ': ' + document.getElementsByName(htmlName)[0].value
          );
          document.getElementsByName(htmlName)[0].value = '';
        }
        if (document.getElementsByName(htmlName)[1]) {
          console.log(
            htmlName + ' ' + document.getElementsByName(htmlName)[1].value
          );
          document.getElementsByName(htmlName)[1].value = '';
        }
      });
      newProduct.customizations.forEach((customization) => {
        writtenName = customization.name;
        htmlName = convertCustomizationName(writtenName);

        if (document.getElementsByName(htmlName)[0]) {
          document.getElementsByName(htmlName)[0].value = '';
        }
        if (document.getElementsByName(htmlName)[1]) {
          document.getElementsByName(htmlName)[1].value = '';
        }
      });

      if (newProduct.variations.length === 1) {
        setFormValues({
          ...formValues,
          masterOrderItemId: value,
          variationId: newProduct.variations[0].variationId,
          [customizations]: customizationValues,
          productType: productType,
          package: newPackageValue,
        });
      } else {
        setFormValues({
          ...formValues,
          masterOrderItemId: value,
          [customizations]: customizationValues,
          productType: productType,
          package: newPackageValue,
        });
      }
    } else {
      setFormValues({
        ...formValues,
        [inputName]: value,
        //Resets customization values - only on product selection change
        [customizations]: customizationValues,
        productType: productType,
      });
    }

    if (inputName === 'fingerSize' && typeof playerData === 'object') {
      playerData.fingerSize = value;
    }
    // checkFormValidation();
  };
  const convertCustomizationName = (asWrittenName) => {
    let nameWords = asWrittenName.split(' ');
    let htmlName = '';
    nameWords.forEach((word, idx) => {
      if (idx > 0) {
        htmlName += word.charAt(0).toUpperCase() + word.slice(1);
      } else {
        htmlName += word.toLowerCase();
      }
    });
    return htmlName;
  };

  const handlePackageValidation = () => {
    if (
      document.getElementById('nameplateFirstName').value === '' ||
      document.getElementById('nameplateFirstName').value === null ||
      document.getElementById('nameplateLastName').value === '' ||
      document.getElementById('nameplateLastName').value === null
    ) {
      if (document.getElementById('add')) {
        document.getElementById('add').disabled = true;
      }
      if (document.getElementById('update')) {
        document.getElementById('update').disabled = true;
      }
    } else {
      if (document.getElementById('add')) {
        document.getElementById('add').removeAttribute('disabled');
      }
      if (document.getElementById('update')) {
        document.getElementById('update').removeAttribute('disabled');
      }
    }
  };

  const handleBatchInputChange = (selectedRecords, inputName, value) => {
    // checkFormValidation();
    selectedRecords.map((record) =>
      setFormValues({
        ...formValues,
        [inputName]: value,
        productType: productType,
      })
    );
  };

  const handleCustomInputChange = (
    inputName,
    value,
    productCustomizationId
  ) => {
    // checkFormValidation();
    const customValues = product.customizations;
    const filteredCustomValue = customValues.filter((customValue) => {
      return (
        (customValue.productCustomizationId !== null &&
          customValue.productCustomizationId === productCustomizationId) ||
        (customValue.productCustomizationId === null &&
          customValue.name.toLowerCase() === inputName.toLowerCase())
      );
    });
    if (filteredCustomValue.length > 0) {
      const newCustomValue = {
        ...filteredCustomValue[0],
        text: value.toUpperCase(),
        name: filteredCustomValue[0].name,
      };
      let filterFormCustomValue = [];
      if (formValues.customizations.length > 0) {
        filterFormCustomValue = formValues.customizations.filter((custom) => {
          const isSameId =
            custom.productCustomizationId !== null &&
            newCustomValue.productCustomizationId !== null &&
            custom.productCustomizationId ===
              newCustomValue.productCustomizationId;

          const isSameName =
            custom.name.toLowerCase() === newCustomValue.name.toLowerCase();

          return !isSameId && !isSameName;
        });
      }
      filterFormCustomValue.push(newCustomValue);
      setFormValues({ ...formValues, customizations: filterFormCustomValue });
    }
  };

  const handleCustomInputInsideEngravingChange = (inputNameList, value) => {
    // checkFormValidation();
    const customValues = product.customizations;
    let filterFormCustomValue = formValues.customizations;
    inputNameList.map((inputName) => {
      const filteredCustomValue = customValues.filter((customValue) => {
        return customValue.name.toLowerCase() === inputName.toLowerCase();
      });
      if (filteredCustomValue.length > 0) {
        const newCustomValue = {
          ...filteredCustomValue[0],
          text: value.toUpperCase(),
          name: filteredCustomValue[0].name,
        };
        let indexCustomizations = filterFormCustomValue.findIndex(
          (custom) => custom.name === newCustomValue.name
        );
        if (indexCustomizations > -1) {
          filterFormCustomValue[indexCustomizations] = newCustomValue;
        }
      }
    });
    setFormValues({ ...formValues, customizations: filterFormCustomValue });
  };

  const handlePackageInputChange = (inputName, value) => {
    let nameplateFirstName = 'First Name';
    let nameplateLastname = 'Last Name';
    let tShirtSize = 'T-Shirt Size';
    let jerseyNumberPendant = 'Jersey Number Pendant';
    if (inputName === 'nameplateFirstName') {
      inputName = nameplateFirstName;
    }
    if (inputName === 'nameplateLastName') {
      inputName = nameplateLastname;
    }
    if (inputName === 'tShirtSize') {
      inputName = tShirtSize;
    }
    if (inputName === 'jerseyNumberPendant') {
      inputName = jerseyNumberPendant;
    }
    const customValues = formValues.package.customizations;
    const filteredCustomValue = customValues.filter((customValue) => {
      return customValue.name.toLowerCase() === inputName.toLowerCase();
    });
    if (filteredCustomValue.length > 0) {
      const newCustomValue = {
        ...filteredCustomValue[0],
        name: filteredCustomValue[0].name,
        text: value.toUpperCase(),
      };
      filteredCustomValue[0].text = newCustomValue.text;
    }

    checkFormValidation();
  };

  const lookupVariationDescription = (masterOrderItemId, variationId) => {
    // Set the variation ID based on the variation description
    let product = roster.products.find(
      (product) => product.masterOrderItemId === masterOrderItemId
    );
    let variant = product.variations.find(
      (variant) => variant.variationId === variationId
    );
    if (variant == null) {
      variant = product.variations[0];
    }

    return variant.description;
  };

  const onCancelClick = () => {
    if (editMode) {
      cancelEdit();
    } else {
      clearForm();
    }
    closeModal && closeModal();
  };

  /*function removeDuplicates(arr)
  {
    return arr.filter(())
  }*/

  const onUpdateClick = (values) => {
    if (validForm === true) {
      values.variation = lookupVariationDescription(
        values.masterOrderItemId,
        values.variationId
      );
      if (roster.packages.length == 0) {
        values.package = null;
      }
      //values.name = values.name.replace("\t", " ");

      values.studentName = replaceTabs(values.studentName);
      values.studentPhone = replaceTabs(values.studentPhone);
      values.studentEmail = replaceTabs(values.studentEmail);
      values.addressLine1 = replaceTabs(values.addressLine1);
      values.addressLine2 = replaceTabs(values.addressLine2);
      values.city = replaceTabs(values.city);
      values.postalCode = replaceTabs(values.postalCode);
      if (values.customizations != null) {
        console.log(product);

        let customizationSortOrder = [];
        product.customizations.map((customization) => {
          console.log(customization);
          customizationSortOrder.push(customization.name);
        });
        customizationSortOrder.push(
          'Engraving Line 1',
          'Engraving line 2',
          'Engraving Line 3',
          'Finger Size',
          'Package'
        );

        console.log(customizationSortOrder);
        customizationSortOrder = customizationSortOrder.map((name) =>
          name.toLowerCase()
        );
        customizationSortOrder = customizationSortOrder.filter(
          (item, index) => customizationSortOrder.indexOf(item) === index
        );
        console.log(customizationSortOrder);
        let updatedCustomizations = [...values.customizations];
        console.log(values.customization);
        values.customizations.map(
          (customization, idx) => (
            console.log(customization),
            (customization.text = replaceTabs(customization.text))
          )
        );
        updatedCustomizations = updatedCustomizations.sort((a, b) => {
          const indexA = customizationSortOrder.indexOf(a.name.toLowerCase());
          console.log(indexA);
          const indexB = customizationSortOrder.indexOf(b.name.toLowerCase());
          console.log(indexB);
          return (
            (indexA === -1 ? Number.MAX_VALUE : indexA) -
            (indexB === -1 ? Number.MAX_VALUE : indexB)
          );
        });
        console.log(updatedCustomizations);
        values.customizations = updatedCustomizations;
      }
      if (values.package != null) {
        if (values.package.customizations != null) {
          values.package.customizations.map(
            (customization, idx) => (
              console.log(customization),
              (customization.text = replaceTabs(customization.text))
            )
          );
        }
      }
      updateRosterLine(rosterKey, values.id, values);
      updatePlayer(values);
      clearForm();
      document.getElementById('mainForm').reset();
      setValidationChecked(false);
      setValidForm(false);
      closeModal && closeModal();
    }
  };

  const replaceTabs = (fieldName) => {
    if (fieldName != null) {
      fieldName = fieldName.replace('\t', ' ');
    }
    return fieldName;
  };

  const onUpdatesClick = (values, variationId, variation, playerPackage) => {
    if (validForm === true) {
      values.map(
        (indValue, idx) => (
          (indValue.masterOrderItemId = product.masterOrderItemId),
          (indValue.variation = variation),
          (indValue.variation = lookupVariationDescription(
            values[idx].masterOrderItemId,
            variationId
          )),
          (indValue.package = playerPackage),
          updateRosterLine(rosterKey, indValue.id, indValue),
          updatePlayer(indValue)
        )
      );
      let noProductRows = document.getElementsByClassName('NoProduct');
      for (let i = 0; i < noProductRows.length; i++) {
        noProductRows[i].style.color = 'black';
        noProductRows[i].style.backgroundColor = '#f8f9fa';
      }
      playerSelectCount = 0;
      //setPlayerSelectCount(playerSelectCount);
      setPlayerSelectCount(0);
      document.getElementById('unselectAll').disabled = true;
      selectedPlayers.length = 0;
      clearForm();
      document.getElementById('mainForm').reset();
      setValidationChecked(false);
      setValidForm(false);
      closeModal && closeModal();
    }
  };

  const onSubmitClick = async (values) => {
    if (validForm === true) {
      // If the closeModal function was not passed in, then open the "adding" modal from this page
      if (!showAddModal) {
        setShowAddModal(true);
        // setWorking will turn the spinner on within the "adding" modal
        setWorking(true);
        document.getElementById('mainForm').style.display = 'none';
      }
      try {
        values.variation = lookupVariationDescription(
          values.masterOrderItemId,
          values.variationId
        );

        let response = await addRosterLine(rosterKey, values);
        values.id = response.data.id;

        values.studentName = replaceTabs(values.studentName);
        values.studentPhone = replaceTabs(values.studentPhone);
        values.studentEmail = replaceTabs(values.studentEmail);
        values.addressLine1 = replaceTabs(values.addressLine1);
        values.addressLine2 = replaceTabs(values.addressLine2);
        values.city = replaceTabs(values.city);
        values.postalCode = replaceTabs(values.postalCode);
        if (values.customizations != null) {
          values.customizations.map(
            (customization, idx) =>
              (customization.text = replaceTabs(customization.text))
          );
        }
        if (values.package != null) {
          if (values.package.customizations != null) {
            values.package.customizations.map(
              (customization, idx) => (
                console.log(customization),
                (customization.text = replaceTabs(customization.text))
              )
            );
          }
        }
        if (roster.accessLevel === 'Student') {
          const selectedProduct = roster.products.find(
            (rosterProduct) =>
              rosterProduct.masterOrderItemId === values.masterOrderItemId
          );
          setSubmittedOrder({
            studentName: values.studentName,
            studentEmail: values.studentEmail,
            studentPhone: values.studentPhone,
            productName: selectedProduct ? selectedProduct.title : '',
            variation: values.variation,
            fingerSize: values.fingerSize,
            customizations: values.customizations
              ? values.customizations.map((customization) => ({
                  name: customization.name,
                  text: customization.text,
                }))
              : [],
            package: values.package
              ? {
                  name: values.package.name,
                  customizations: values.package.customizations
                    ? values.package.customizations.map((customization) => ({
                        name: customization.name,
                        text: customization.text,
                      }))
                    : [],
                }
              : null,
          });
          setWorking(false);
          return;
        }

        setTimeout(() => {
          setShowAddModal(false);
          document.getElementById('mainForm').style.display = 'block';

          setFormKey((k) => k + 1);
        }, 1500);
        setWorking(false);
        // If the addPlayer function was passed in then call it to add the player to the list displayed on the screen
        addPlayer && addPlayer(values);
        clearForm();
        document.getElementById('mainForm').reset();

        if (roster.products.length === 1) {
          if (roster.accessLevel === 'Coach') {
            handleBatchInputChange(
              selectedPlayers,
              'masterOrderItemId',
              roster.products[0].masterOrderItemId
            );
          } else {
            handleInputChange(
              'masterOrderItemId',
              roster.products[0].masterOrderItemId
            );
          }
          setItemId(roster.products[0].masterOrderItemId);
          setSelectedProduct(roster.products[0].masterOrderItemId);
          setFormValues({
            ...formValues,
            masterOrderItemId: roster.products[0].masterOrderItemId,
          });
        }
        setValidationChecked(false);
        setValidForm(false);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const clearForm = () => {
    let newFormValues = {
      sortedIndex: null,
      id: '',
      rosterId: roster.id,
      masterOrderItemId: '',
      subOrderItemRowId: '',
      variation: '',
      variationId: '',
      studentName: '',
      studentEmail: '',
      studentPhone: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      countryCode: '',
      state: '',
      postalCode: '',
      fingerSize: null,
      customizations: [],
      editable: true,
      productType: null,
      package: null,
      optionalEngraving: false,
      isAddon: null,
      createdDate: null,
    };
    setFormValues(newFormValues);
    setSelectedProduct('');
    setProduct('');
    setItemId('');
    setProductType('');
    setPackageHasNameplate(false);
    setPackageHasAccessories(false);
    if (playerData && typeof playerData === 'object') {
      playerData.fingerSize = null;
    }
  };

  const handleAddAnotherOrder = () => {
    setShowAddModal(false);
    setSubmittedOrder(null);
    clearForm();

    const form = document.getElementById('mainForm');
    if (form) {
      form.reset();
      form.style.display = 'block';
    }

    setFormKey((k) => k + 1);

    if (roster.products.length === 1) {
      const onlyProduct = roster.products[0];
      setProduct(onlyProduct);
      setProductType(onlyProduct.productType);
      setItemId(onlyProduct.masterOrderItemId);
      setSelectedProduct(onlyProduct.masterOrderItemId);
      setFormValues((currentValues) => ({
        ...currentValues,
        masterOrderItemId: onlyProduct.masterOrderItemId,
        productType: onlyProduct.productType,
      }));
    }

    setValidationChecked(false);
    setValidForm(false);
  };

  const handleAddModalHide = () => {
    if (roster.accessLevel === 'Student') {
      handleAddAnotherOrder();
    } else {
      setShowAddModal(false);
    }
  };

  // New Form
  const [validationChecked, setValidationChecked] = useState(false);
  const [validForm, setValidForm] = useState(false);

  const checkFormValidation = () => {
    const form = document.getElementById('mainForm');
    if (document.getElementById('update')) {
      document.getElementById('update').removeAttribute('disabled');
    }
    if (document.getElementById('add')) {
      document.getElementById('add').removeAttribute('disabled');
    }
    if (document.getElementById('variationId')) {
      if (
        document.getElementById('variationId').value !== '' &&
        document.getElementById('variationId').value !== null
      ) {
        //playerData.variationId = '1';
      } else {
        if (playerData.variationId !== '' && playerData.variationId !== null) {
          document.getElementById('variationId').value = playerData.variationId;
        }
      }
    }
    if (document.getElementById('fingerSize')) {
      if (playerData.fingerSize !== '' && playerData.fingerSize != null) {
        document.getElementById('fingerSize').value = playerData.fingerSize;
      }
    }

    if (form.checkValidity() === true) {
      setValidForm(true);
    } else {
      setValidForm(true);
      const elements = Array.from(form.elements);
      elements.forEach((el) => {
        if (!el.checkValidity()) {
          console.warn(
            `Invalid Input: ${el.name || el.id}; Value: ${el.value}`,
            el.validationMessage
          );
          if (el.id === 'engravingLine1') {
            if (document.getElementById('radio-yes')?.checked) {
              if (el.value.trim() === '' || el.value === null) {
                setValidForm(false);
              }
            }
          } else if (el.id === 'engravingLine2') {
            if (
              document.getElementById('radio-yes')?.checked &&
              (el.value === null || el.value === '')
            ) {
              setValidForm(false);
            }
          } else {
            setValidForm(false);
          }
        }
      });
    }

    if (
      (document.getElementById('radio-yes')?.checked ||
        !document.getElementById('radio-yes')) &&
      (document.getElementById('engravingLine1')?.value.trim() === '' ||
        document.getElementById('engravingLine1')?.value === null)
    ) {
      if (document.getElementById('update')) {
        document.getElementById('update').disabled = true;
      }
      if (document.getElementById('add')) {
        document.getElementById('add').disabled = true;
      }
      setValidForm(false);
    }
  };

  const handleSubmit = (event) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();
    if (form.checkValidity() === false) {
      setValidationChecked(true);
      setValidForm(false);
    } else {
      setValidationChecked(true);
      setValidForm(true);
    }
  };

  const checkPackageCustomizations = (pkg) => {
    setPackageHasNameplate(false);
    setPackageHasAccessories(false);
    if (pkg === 'Dynasty' || pkg === 'Ultimate' || pkg === 'Champions+') {
      setPackageHasNameplate(true);
    }
    if (pkg === 'Ultimate') {
      setPackageHasAccessories(true);
    }
  };

  const buildPackageSelection = (packageName) => {
    if (packageName === 'Ultimate') {
      return {
        name: packageName,
        customizations: [
          { name: 'First Name', text: null },
          { name: 'Last Name', text: null },
          { name: 'T-Shirt Size', text: null },
          { name: 'Jersey Number Pendant', text: null },
        ],
      };
    }

    if (packageName === 'Champions+' || packageName === 'Dynasty') {
      return {
        name: packageName,
        customizations: [
          { name: 'First Name', text: null },
          { name: 'Last Name', text: null },
        ],
      };
    }

    return { name: packageName, customizations: null };
  };

  const handlePackageSelection = (packageName) => {
    handleInputChange('package', buildPackageSelection(packageName));
    checkPackageCustomizations(packageName);
  };

  const selectedPackageName =
    formValues.package && typeof formValues.package === 'object'
      ? formValues.package.name
      : '';
  const packageSelectionDisabled =
    !formValues.editable || (isCoachApproval && !adminUser);
  const packageAvailability = {
    Champions: championsPackageAvailable,
    'Champions+': championsPlusPackageAvailable,
    Dynasty: dynastyPackageAvailable,
    Ultimate: ultimatePackageAvailable,
  };
  const availablePackageCards = PACKAGE_CARD_DETAILS.filter(
    (packageDetails) => packageAvailability[packageDetails.name]
  );

  const handleDynastyUpgrade = () => {
    if (packageSelectionDisabled || !dynastyPackageAvailable) {
      return;
    }

    handlePackageSelection('Dynasty');
    window.requestAnimationFrame(() => {
      dynastyPackageRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      dynastyPackageRef.current
        ?.querySelector('input')
        ?.focus({ preventScroll: true });
    });
  };

  const renderTooltip = () => (
    <Tooltip id="tooltip" className="custom-tooltip">
      <span className="tooltip-icon">!</span>Character limit reached
    </Tooltip>
  );

  useEffect(() => {
    if (playerData.package) {
      checkPackageCustomizations(playerData.package.name);
    }
    // checkFormValidation();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    checkFormValidation();
  }, [formValues]);

  return (
    <>
      <Form
        key={formKey}
        id="mainForm"
        className={
          roster.accessLevel === 'Student'
            ? 'student-ring-selection-form'
            : roster.accessLevel === 'Coach'
            ? 'coach-ring-selection-form'
            : undefined
        }
        noValidate
        validated={validationChecked}
        onSubmit={handleSubmit}
      >
        <Card border="secondary" className="ringSelectionCard">
          <Card.Header bg="secondary" text="white" className="cardHeader">
            {individualSelection === true && (
              <Form.Group md="4">
                <Form.Label>Team Member Name</Form.Label>
                <InputGroup hasValidation>
                  <Form.Control
                    required
                    type="text"
                    name="studentName"
                    placeholder="Name"
                    defaultValue={playerData.studentName}
                    onChange={(e) =>
                      handleInputChange('studentName', e.target.value)
                    }
                    className="headerInput"
                    disabled={!formValues.editable}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please enter team member name.
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            )}

            {roster.accessLevel === 'Coach' && individualSelection === true && (
              <>
                <Form.Group md="4">
                  <Form.Label>Team Member Email (Optional)</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      type="email"
                      name="studentEmail"
                      placeholder="Email"
                      defaultValue={playerData.studentEmail}
                      onChange={(e) =>
                        handleInputChange('studentEmail', e.target.value)
                      }
                      className="headerInput"
                      disabled={!formValues.editable}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter a valid email.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Form.Group md="4">
                  <Form.Label>Team Member Phone (Optional)</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      type="tel"
                      pattern="(\d|[()-]|\s)+"
                      name="studentPhone"
                      id="studentPhone"
                      placeholder="Phone Number"
                      defaultValue={playerData.studentPhone}
                      onChange={(e) =>
                        handleInputChange('studentPhone', e.target.value)
                      }
                      className="headerInput"
                      disabled={!formValues.editable}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter a valid phone number.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </>
            )}
            {roster.accessLevel === 'Student' && (
              <>
                <Form.Group md="4">
                  <Form.Label>Team Member Email (Optional)</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      type="email"
                      name="studentEmail"
                      placeholder="Email"
                      defaultValue={playerData.studentEmail}
                      onChange={(e) =>
                        handleInputChange('studentEmail', e.target.value)
                      }
                      className="headerInput"
                      disabled={!formValues.editable}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter a valid email.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Form.Group md="4">
                  <Form.Label>Team Member Phone</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      type="tel"
                      pattern="(\d|[()-]|\s)+"
                      name="studentPhone"
                      id="studentPhone"
                      placeholder="Phone Number"
                      defaultValue={playerData.studentPhone}
                      onChange={(e) =>
                        handleInputChange('studentPhone', e.target.value)
                      }
                      className="headerInput"
                      required
                      disabled={!formValues.editable}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter a valid phone number.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </>
            )}

            {roster.shippingTypeName == 'Individual Shipping' && (
              <>
                <Form.Group md="4">
                  <Form.Label>Country/Region</Form.Label>
                  <InputGroup required hasValidation>
                    <Form.Control
                      required={
                        roster.shippingTypeName === 'Individual Shipping'
                      }
                      as="select"
                      name="countryCode"
                      id="countryCode"
                      defaultValue={'US'}
                      onChange={(e) =>
                        handleInputChange('countryCode', e.target.value)
                      }
                      value={formValues.countryCode}
                      disabled={!formValues.editable}
                    >
                      <option value="">Please make a selection</option>
                      {countries.map((item, idx) => (
                        <option key={idx} value={item.countryCode}>
                          {item.countryCode} - {item.name}
                        </option>
                      ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                      Required.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Form.Group md="4">
                  <Form.Label>Address Line 1</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      required={
                        roster.shippingTypeName === 'Individual Shipping'
                      }
                      type="text"
                      name="addressLine1"
                      placeholder="Address Line 1"
                      defaultValue={playerData.addressLine1}
                      onChange={(e) =>
                        handleInputChange('addressLine1', e.target.value)
                      }
                      className="headerInput"
                      disabled={!formValues.editable}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter Address Line 1.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Form.Group md="4">
                  <Form.Label>Address Line 2 (Optional)</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      type="text"
                      name="addressLine2"
                      placeholder="Address Line 2"
                      defaultValue={playerData.addressLine2}
                      onChange={(e) =>
                        handleInputChange('addressLine2', e.target.value)
                      }
                      className="headerInput"
                      disabled={!formValues.editable}
                    />
                  </InputGroup>
                </Form.Group>
                <Form.Group md="4">
                  <Form.Label>City</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      required={
                        roster.shippingTypeName === 'Individual Shipping'
                      }
                      type="text"
                      name="city"
                      placeholder="City"
                      defaultValue={playerData.city}
                      onChange={(e) =>
                        handleInputChange('city', e.target.value)
                      }
                      className="headerInput"
                      disabled={!formValues.editable}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter City.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Form.Group md="4">
                  <Form.Label>State</Form.Label>
                  <InputGroup required hasValidation>
                    <Form.Control
                      required={
                        roster.shippingTypeName === 'Individual Shipping'
                      }
                      as="select"
                      name="state"
                      id="state"
                      defaultValue={playerData.state ? playerData.state : ''}
                      onChange={(e) =>
                        handleInputChange('state', e.target.value)
                      }
                      value={formValues.state}
                      disabled={!formValues.editable}
                    >
                      <option value="">Please make a selection</option>
                      {states.map((item, idx) => (
                        <option key={idx} value={item.regionCode}>
                          {item.regionCode} - {item.name}
                        </option>
                      ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                      Required.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Form.Group md="4">
                  <Form.Label>Postal Code</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      required={
                        roster.shippingTypeName === 'Individual Shipping'
                      }
                      type="text"
                      name="postalCode"
                      placeholder="Postal Code"
                      defaultValue={playerData.postalCode}
                      onChange={(e) =>
                        handleInputChange('postalCode', e.target.value)
                      }
                      className="headerInput"
                      disabled={!formValues.editable}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please enter Postal Code.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </>
            )}
          </Card.Header>
          <Card.Body className="cardBody">
            <Form.Group>
              <Form.Row>
                <Col>
                  <Form.Label>Product</Form.Label>
                  {productList.length > 2 ? (
                    individualSelection === true ||
                    roster.accessLevel === 'Student' ? (
                      <>
                        <Form.Control
                          required
                          as="select"
                          onChange={(e) => {
                            handleInputChange(
                              'masterOrderItemId',
                              e.target.value,
                              'customizations',
                              [],
                              'package',
                              null
                            );

                            setItemId(e.target.value);

                            return setSelectedProduct(e.target.value);
                          }}
                          name="masterOrderItemId"
                          value={itemId}
                          custom
                          disabled={!formValues.editable}
                        >
                          {productList.map((product, idx) => (
                            <option key={idx} value={product.masterOrderItemId}>
                              {product.title}
                            </option>
                          ))}
                        </Form.Control>
                        <Form.Control.Feedback type="invalid">
                          Please select a product.
                        </Form.Control.Feedback>
                      </>
                    ) : (
                      <>
                        <Form.Control
                          required
                          as="select"
                          onChange={(e) => {
                            handleBatchInputChange(
                              selectedPlayers,
                              'masterOrderItemId',
                              e.target.value
                            );

                            setItemId(e.target.value);

                            return setSelectedProduct(e.target.value);
                          }}
                          name="masterOrderItemId"
                          value={itemId}
                          custom
                          disabled={!formValues.editable}
                        >
                          {productList.map((product, idx) => (
                            <option key={idx} value={product.masterOrderItemId}>
                              {product.title}
                            </option>
                          ))}
                        </Form.Control>
                      </>
                    )
                  ) : (
                    <>
                      <div>{productList[1]?.title}</div>
                      <input
                        type="hidden"
                        value={productList[1]?.masterOrderItemId}
                      />
                    </>
                  )}
                </Col>
              </Form.Row>
              <Form.Row>
                {selectedProduct && product && (
                  <RingInputSection
                    handleInputChange={(inputName, value) =>
                      handleInputChange(inputName, value)
                    }
                    handleCustomInputChange={(
                      inputName,
                      value,
                      productCustomizationId
                    ) =>
                      handleCustomInputChange(
                        inputName,
                        value,
                        productCustomizationId
                      )
                    }
                    handleCustomInputInsideEngravingChange={(
                      inputNameList,
                      value
                    ) =>
                      handleCustomInputInsideEngravingChange(
                        inputNameList,
                        value
                      )
                    }
                    editFormValues={playerData}
                    product={product}
                    individualSelection={individualSelection}
                    view={roster.accessLevel}
                    internalView={internalView}
                    paymentType={roster.paymentType}
                    adminUser={adminUser}
                    editAddress={editAddress}
                    isCoachApproval={isCoachApproval}
                    isCustomerOsr={roster.isCustomerOsr}
                    selectedPackageName={selectedPackageName}
                    dynastyPackageAvailable={dynastyPackageAvailable}
                    packageSelectionDisabled={packageSelectionDisabled}
                    onSelectDynasty={handleDynastyUpgrade}
                  />
                )}
              </Form.Row>
              {packageProductType && roster.packages.length > 0 && (
                <Form.Row>
                  <div className="spacerDivWithLine" />
                  <Col>
                    <Row className="package-section-heading align-items-center">
                      <Col>
                        <h4>Choose a Package</h4>
                        <p className="package-section-intro">
                          Your base product is included. Upgrade to add lifetime
                          protection &amp; extras.
                        </p>
                      </Col>
                      <Col xs="auto">
                        <Button
                          type="button"
                          variant="link"
                          className="package-details-link"
                          onClick={() => {
                            setShowPackageModal(true);
                            document.getElementById(
                              'mainModal'
                            ).style.visibility = 'hidden';
                          }}
                        >
                          Package Details
                        </Button>
                      </Col>
                    </Row>

                    <div className="package-upgrade-grid" id="packageRadios">
                      {availablePackageCards.map((packageDetails) => {
                        const packageInputId =
                          'package-' +
                          packageDetails.name.replace('+', 'plus').toLowerCase();
                        const packagePrice = getPackagePrice(
                          roster.packages,
                          packageDetails.name
                        );
                        const previousVisiblePackageName =
                          packageDetails.defaultPreviousPackages
                            .slice()
                            .reverse()
                            .find((packageName) =>
                              packageName === 'Basic'
                                ? basicPackageAvailable
                                : packageAvailability[packageName]
                            ) || null;
                        const previousVisiblePackage =
                          availablePackageCards.find(
                            (visiblePackage) =>
                              visiblePackage.name ===
                              previousVisiblePackageName
                          ) || null;
                        const defaultPreviousPackage =
                          packageDetails.defaultPreviousPackages[
                            packageDetails.defaultPreviousPackages.length - 1
                          ];
                        const usesDefaultValueLadder =
                          previousVisiblePackageName ===
                          defaultPreviousPackage;
                        const previousBenefitIds = new Set(
                          previousVisiblePackage?.includedBenefits.map(
                            (benefit) => benefit.id
                          ) || []
                        );
                        const displayedBenefits = usesDefaultValueLadder
                          ? packageDetails.defaultBenefits
                          : packageDetails.includedBenefits.filter(
                              (benefit) => !previousBenefitIds.has(benefit.id)
                            );
                        let displayedValueLead = `${packageDetails.name.toUpperCase()} INCLUDES`;

                        if (usesDefaultValueLadder) {
                          displayedValueLead = packageDetails.valueLead;
                        } else if (previousVisiblePackageName) {
                          displayedValueLead = `EVERYTHING IN ${previousVisiblePackageName.toUpperCase()}, PLUS`;
                        }

                        return (
                          <div
                            className={
                              packageDetails.isHero
                                ? 'package-card-option package-card-option--hero'
                                : 'package-card-option'
                            }
                            data-package-tier={packageDetails.name}
                            key={packageDetails.name}
                            ref={
                              packageDetails.name === 'Dynasty'
                                ? dynastyPackageRef
                                : null
                            }
                          >
                            {packageDetails.isHero && (
                              <span className="best-value-ribbon">
                                Best Value
                              </span>
                            )}
                            <Form.Check
                              required
                              type="radio"
                              className="package-card-radio"
                            >
                              <Form.Check.Input
                                required
                                type="radio"
                                value={packageDetails.name}
                                id={packageInputId}
                                name="package"
                                checked={
                                  selectedPackageName === packageDetails.name
                                }
                                onChange={() =>
                                  handlePackageSelection(packageDetails.name)
                                }
                                disabled={packageSelectionDisabled}
                              />
                              <Form.Check.Label
                                className="package-card-label"
                                htmlFor={packageInputId}
                              >
                                <div className="package-card-top">
                                  <Image
                                    fluid
                                    className="package-card-image"
                                    src={packageDetails.image}
                                    alt={packageDetails.name + ' package'}
                                  />
                                  <div>
                                    <h5 className="package-card-title">
                                      {packageDetails.name}
                                    </h5>
                                    <span className="package-card-subtitle">
                                      {packageDetails.label}
                                    </span>
                                    <div className="package-card-price">
                                      <span className="package-add-on-price">
                                        {formatPackagePrice(packagePrice)}
                                      </span>
                                      <span className="package-savings">
                                        Save {'$' + packageDetails.savings}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <p className="package-value-lead">
                                  {displayedValueLead}
                                </p>
                                <ul className="package-benefit-list">
                                  {displayedBenefits.map((benefit) => (
                                    <li key={benefit.id}>{benefit.text}</li>
                                  ))}
                                </ul>
                              </Form.Check.Label>
                            </Form.Check>
                          </div>
                        );
                      })}
                    </div>

                    {basicPackageAvailable && (
                      <div
                        className="package-basic-option"
                        data-package-tier="Basic"
                      >
                        <Form.Check
                          required
                          type="radio"
                          className="package-basic-radio"
                        >
                          <Form.Check.Input
                            required
                            type="radio"
                            value="Basic"
                            id="package-basic"
                            name="package"
                            checked={selectedPackageName === 'Basic'}
                            onChange={() => handlePackageSelection('Basic')}
                            disabled={packageSelectionDisabled}
                          />
                          <Form.Check.Label
                            className="package-basic-label"
                            htmlFor="package-basic"
                          >
                            <span>
                              <strong>{'Basic \u2014 ring only'}</strong>
                              <small>{''}</small>
                            </span>
                            <span className="package-basic-price">
                              {formatPackagePrice(
                                getPackagePrice(roster.packages, 'Basic')
                              )}{' '}
                              {'\u00b7 limited warranty'}
                            </span>
                          </Form.Check.Label>
                        </Form.Check>
                      </div>
                    )}

                    {validationChecked && !selectedPackageName && (
                      <div className="invalid-feedback d-block">
                        Please select a package.
                      </div>
                    )}
                  </Col>
                </Form.Row>
              )}
              {packageHasNameplate && individualSelection && (
                <Form.Row>
                  <div className="spacerDivWithLine" />

                  <Col className="column1">
                    <h5>Nameplate Engraving</h5>

                    <Image
                      fluid
                      src={selectedNameplateImage}
                      alt="nameplate"
                      className="nameplateMobile"
                    />
                    {isShowMaxLength ? (
                      <Form.Group md="4">
                        <Form.Label>First Name</Form.Label>
                        <InputGroup required hasValidation>
                          <OverlayTrigger
                            trigger={['click']}
                            placement="bottom"
                            overlay={renderTooltip()}
                            show={showTooltip && valueFirstName.length >= 25}
                          >
                            <Form.Control
                              required={true}
                              type="text"
                              name="nameplateFirstName"
                              id="nameplateFirstName"
                              placeholder="Max 25 Characters"
                              maxLength="25"
                              defaultValue={
                                playerData.package &&
                                'package' in playerData &&
                                playerData.package.customizations &&
                                'customizations' in playerData.package &&
                                playerData.package.customizations.length > 0
                                  ? playerData.package.customizations[0].text
                                  : ''
                              }
                              onChange={(e) => {
                                handlePackageInputChange(
                                  e.target.name,
                                  e.target.value
                                );
                                setValueFirstName(e.target.value);
                                if (e.target.value.length >= 25)
                                  setShowTooltip(true);
                                setTimeout(() => {
                                  setShowTooltip(false);
                                }, 2000);
                              }}
                              onInput={(e) => handlePackageValidation()}
                              className="headerInput"
                              style={{ textTransform: 'uppercase' }}
                              ref={inputRef}
                              onClick={() =>
                                handleInputClick(valueFirstName, 'First Name')
                              }
                              disabled={
                                !formValues.editable || (isCoachApproval && !adminUser)
                              }
                            />
                          </OverlayTrigger>
                          <Form.Control.Feedback type="invalid">
                            Required.
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Form.Group>
                    ) : (
                      <Form.Group md="4">
                        <Form.Label>First Name</Form.Label>
                        <InputGroup required hasValidation>
                          <Form.Control
                            required={true}
                            type="text"
                            name="nameplateFirstName"
                            id="nameplateFirstName"
                            defaultValue={
                              playerData.package &&
                              'package' in playerData &&
                              playerData.package.customizations &&
                              'customizations' in playerData.package &&
                              playerData.package.customizations.length > 0
                                ? playerData.package.customizations[0].text
                                : ''
                            }
                            onChange={(e) => {
                              handlePackageInputChange(
                                e.target.name,
                                e.target.value
                              );
                              setValueFirstName(e.target.value);
                              if (e.target.value.length >= 25)
                                setShowTooltip(true);
                              setTimeout(() => {
                                setShowTooltip(false);
                              }, 2000);
                            }}
                            onInput={(e) => handlePackageValidation()}
                            className="headerInput"
                            style={{ textTransform: 'uppercase' }}
                            ref={inputRef}
                            onClick={() =>
                              handleInputClick(valueFirstName, 'First Name')
                            }
                            disabled={
                              !formValues.editable || (isCoachApproval && !adminUser)
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            Required.
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Form.Group>
                    )}
                    {isShowMaxLength ? (
                      <Form.Group md="4">
                        <Form.Label>Last Name</Form.Label>
                        <InputGroup hasValidation>
                          <OverlayTrigger
                            trigger={['click']}
                            placement="bottom"
                            overlay={renderTooltip()}
                            show={
                              showTooltipLastName && valueLastName.length >= 25
                            }
                          >
                            <Form.Control
                              required={true}
                              type="text"
                              name="nameplateLastName"
                              id="nameplateLastName"
                              placeholder="Max 25 Characters"
                              maxLength="25"
                              defaultValue={
                                playerData.package &&
                                'package' in playerData &&
                                playerData.package.customizations &&
                                'customizations' in playerData.package &&
                                playerData.package.customizations.length > 0
                                  ? playerData.package.customizations[1].text
                                  : ''
                              }
                              onChange={(e) => {
                                handlePackageInputChange(
                                  e.target.name,
                                  e.target.value
                                );
                                setValueLastName(e.target.value);
                                if (e.target.value.length >= 25) {
                                  setShowTooltipLastName(true);
                                  setTimeout(() => {
                                    setShowTooltipLastName(false);
                                  }, 2000);
                                }
                              }}
                              onInput={(e) => handlePackageValidation()}
                              className="headerInput"
                              style={{ textTransform: 'uppercase' }}
                              ref={inputRef}
                              onClick={() =>
                                handleInputClick(valueLastName, 'Last Name')
                              }
                              disabled={
                                !formValues.editable || (isCoachApproval && !adminUser)
                              }
                            />
                          </OverlayTrigger>
                          <Form.Control.Feedback type="invalid">
                            Required.
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Form.Group>
                    ) : (
                      <Form.Group md="4">
                        <Form.Label>Last Name</Form.Label>
                        <InputGroup hasValidation>
                          <Form.Control
                            required={true}
                            type="text"
                            name="nameplateLastName"
                            id="nameplateLastName"
                            defaultValue={
                              playerData.package &&
                              'package' in playerData &&
                              playerData.package.customizations &&
                              'customizations' in playerData.package &&
                              playerData.package.customizations.length > 0
                                ? playerData.package.customizations[1].text
                                : ''
                            }
                            onChange={(e) => {
                              handlePackageInputChange(
                                e.target.name,
                                e.target.value
                              );
                              setValueLastName(e.target.value);
                              if (e.target.value.length >= 25) {
                                setShowTooltipLastName(true);
                                setTimeout(() => {
                                  setShowTooltipLastName(false);
                                }, 2000);
                              }
                            }}
                            onInput={(e) => handlePackageValidation()}
                            className="headerInput"
                            style={{ textTransform: 'uppercase' }}
                            ref={inputRef}
                            onClick={() =>
                              handleInputClick(valueLastName, 'Last Name')
                            }
                            disabled={
                              !formValues.editable || (isCoachApproval && !adminUser)
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            Required.
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Form.Group>
                    )}
                  </Col>
                  <Col className="column2">
                    <Image
                      fluid
                      src={selectedNameplateImage}
                      alt="nameplate"
                      className={nameplateImageClass}
                    />
                  </Col>
                </Form.Row>
              )}
              {packageHasAccessories && individualSelection && (
                <>
                  <Form.Row>
                    <div className="spacerDivWithLine" />
                    <Col>
                      <Form.Group md="4">
                        <Form.Label>T-Shirt Size</Form.Label>
                        <InputGroup required hasValidation>
                          <Form.Control
                            required={true}
                            as="select"
                            name="tShirtSize"
                            id="tShirtSize"
                            defaultValue={
                              playerData.package &&
                              playerData.package.customizations &&
                              playerData.package.customizations[2]
                                ? playerData.package.customizations[2].text
                                : ''
                            }
                            onChange={(e) =>
                              handlePackageInputChange(
                                e.target.name,
                                e.target.value
                              )
                            }
                            onInput={(e) => handlePackageValidation()}
                            //disabled={!formValues.editable}
                            disabled={
                              !formValues.editable || (isCoachApproval && !adminUser)
                            }
                          >
                            <option value="">Choose t-shirt size</option>
                            <option value="S">S</option>
                            <option value="M">M</option>
                            <option value="L">L</option>
                            <option value="XL">XL</option>
                            <option value="2XL">2XL</option>
                            <option value="3XL">3XL</option>
                          </Form.Control>
                          <Form.Control.Feedback type="invalid">
                            Required.
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Form.Row>
                  <Form.Row>
                    <div className="spacerDivWithLine" />
                    <Col>
                      <Form.Group md="4">
                        <Form.Label>Jersey Number Pendant</Form.Label>
                        <InputGroup required hasValidation>
                          <Form.Control
                            required={true}
                            as="select"
                            name="jerseyNumberPendant"
                            id="jerseyNumberPendant"
                            defaultValue={
                              playerData.package &&
                              playerData.package.customizations &&
                              playerData.package.customizations[3]
                                ? playerData.package.customizations[3].text
                                : ''
                            }
                            onChange={(e) =>
                              handlePackageInputChange(
                                e.target.name,
                                e.target.value
                              )
                            }
                            onInput={(e) => handlePackageValidation()}
                            //disabled={!formValues.editable}
                            disabled={
                              !formValues.editable || (isCoachApproval && !adminUser)
                            }
                          >
                            <option disabled value="">
                              Choose jersey number
                            </option>
                            {jerseyNumberPendantOptions.map((number) => (
                              <option key={number} value={number}>
                                {number}
                              </option>
                            ))}
                          </Form.Control>
                          <Form.Control.Feedback type="invalid">
                            Required.
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Form.Row>
                </>
              )}
            </Form.Group>
            {!validForm && validationChecked && (
              <div className="alert alert-danger">
                Some fields are missing or incorrectly filled in. Please review
                all fields before submitting.
              </div>
            )}
            <ButtonGroup>
              {editMode && individualSelection === true && (
                <>
                  <Button
                    type="submit"
                    onClick={() => {
                      onUpdateClick(formValues);
                    }}
                    className="m-2"
                    id="update"
                    disabled={!validForm}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Update
                  </Button>
                </>
              )}

              {editMode && individualSelection === false && (
                <>
                  <Button
                    type="submit"
                    onClick={() => {
                      onUpdatesClick(
                        selectedPlayers,
                        formValues.variationId,
                        formValues.variation,
                        formValues.package
                      );
                    }}
                    className="m-2"
                    id="update"
                  >
                    <FontAwesomeIcon icon={faEdit} /> Update
                  </Button>
                </>
              )}

              {!editMode && (
                <>
                  <Button
                    type="submit"
                    variant="success"
                    onClick={() => onSubmitClick(formValues)}
                    className="m-2"
                    id="add"
                    disabled={!validForm}
                  >
                    <FontAwesomeIcon icon={faSave} /> Submit
                  </Button>
                </>
              )}

              <Button
                variant="secondary"
                onClick={() => onCancelClick()}
                className="m-2"
              >
                <FontAwesomeIcon icon={faWindowClose} /> Cancel
              </Button>
            </ButtonGroup>
          </Card.Body>
        </Card>
      </Form>
      <Modal
        animation={false}
        show={showAddModal}
        onHide={handleAddModalHide}
      >
        <Modal.Header closeButton>
          <Modal.Title>{working ? 'Adding...' : 'Order Added'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {working && (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          )}
          {!working && roster.accessLevel === 'Student' && submittedOrder && (
            <>
              <p>
                Thank you, your roster details have been successfully saved! Your
                coach will review and finalize the order next. Below is a summary
                of what you submitted:
              </p>
              <div className="submitted-order-summary">
                <p>
                  <strong>Player</strong>
                  <br />
                  {submittedOrder.studentName}
                  {submittedOrder.studentEmail && (
                    <>
                      <br />
                      {submittedOrder.studentEmail}
                    </>
                  )}
                  {submittedOrder.studentPhone && (
                    <>
                      <br />
                      {submittedOrder.studentPhone}
                    </>
                  )}
                </p>
                <p>
                  <strong>Product & Variant</strong>
                  <br />
                  {submittedOrder.productName}
                  {submittedOrder.variation && ` — ${submittedOrder.variation}`}
                </p>
                <p>
                  <strong>Product Customizations & Finger Size</strong>
                </p>
                <ul>
                  {submittedOrder.customizations
                    .filter((customization) => customization.text)
                    .map((customization) => (
                      <li key={customization.name}>
                        {customization.name}: {customization.text}
                      </li>
                    ))}
                  <li>
                    Finger Size: {submittedOrder.fingerSize || 'Not applicable'}
                  </li>
                </ul>
                <p>
                  <strong>Package</strong>
                  <br />
                  {submittedOrder.package
                    ? submittedOrder.package.name
                    : 'Not selected'}
                </p>
                {submittedOrder.package &&
                  submittedOrder.package.customizations.length > 0 && (
                    <>
                      <p>
                        <strong>Package Customizations</strong>
                      </p>
                      <ul>
                        {submittedOrder.package.customizations
                          .filter((customization) => customization.text)
                          .map((customization) => (
                            <li key={customization.name}>
                              {customization.name}: {customization.text}
                            </li>
                          ))}
                      </ul>
                    </>
                  )}
              </div>
              <p>
                You may now close this page. If you need assistance, please contact{' '}
                <a href="mailto:questions@signaturechampions.com">
                  questions@signaturechampions.com
                </a>
                .
              </p>
            </>
          )}
          {!working && roster.accessLevel !== 'Student' && (
            <>Your order has been received.</>
          )}
        </Modal.Body>
        <Modal.Footer>
          {roster.accessLevel === 'Student' ? (
            !working && (
              <Button variant="success" onClick={handleAddAnotherOrder}>
                Add Another Order
              </Button>
            )
          ) : (
            <Button variant="success" onClick={() => setShowAddModal(false)}>
              OK
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      <Modal
        className="chartModal"
        animation={false}
        show={showPackageModal}
        onHide={() => {
          setShowPackageModal(false);
          document.getElementById('mainModal').style.visibility = 'visible';
        }}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Package Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <PackageComparisonTable packages={roster.packages} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowPackageModal(false);
              document.getElementById('mainModal').style.visibility = 'visible';
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RingSelectionView;
